import type { Component } from "solid-js";
import { createMemo, For, Show } from "solid-js";
import { twJoin } from "tailwind-merge";
import { DurationMonths } from "~/solidJs/shared/api/TeamApi";
import { formatCurrency } from "~/solidJs/shared/helpers/currencyHelpers";

interface DiscountTier {
	minSeats: number;
	yearlyDiscount: number;
	monthlyDiscount: number;
}

const DISCOUNT_TIERS: DiscountTier[] = [
	{ minSeats: 10, yearlyDiscount: 20, monthlyDiscount: 10 },
	{ minSeats: 5, yearlyDiscount: 10, monthlyDiscount: 5 },
	{ minSeats: 2, yearlyDiscount: 5, monthlyDiscount: 0 }
];

const getDiscountForSeats = (seats: number, duration: DurationMonths): number => {
	for (const tier of DISCOUNT_TIERS) {
		if (seats >= tier.minSeats) {
			return duration === DurationMonths.Yearly ? tier.yearlyDiscount : tier.monthlyDiscount;
		}
	}
	return 0;
};

const getNextDiscountTier = (
	seats: number,
	duration: DurationMonths
): { seatsNeeded: number; discount: number } | null => {
	const currentDiscount = getDiscountForSeats(seats, duration);

	// Find the next tier with higher discount
	for (let i = DISCOUNT_TIERS.length - 1; i >= 0; i--) {
		const tier = DISCOUNT_TIERS[i];
		const tierDiscount =
			duration === DurationMonths.Yearly ? tier.yearlyDiscount : tier.monthlyDiscount;

		if (tierDiscount > currentDiscount && seats < tier.minSeats) {
			return {
				seatsNeeded: tier.minSeats - seats,
				discount: tierDiscount
			};
		}
	}
	return null;
};

interface BillingPlanOption {
	duration: DurationMonths;
	label: string;
	price: string | undefined;
	discount_price?: string | null;
	price_per_seat: string | undefined;
	discountPercent: number;
}

interface BillingPlanSelectorProps {
	selectedDuration: DurationMonths;
	onDurationChange: (duration: DurationMonths) => void;
	seats: number;
	yearlyPrice: string | undefined;
	yearlyDiscountPrice?: string | null;
	yearlyPricePerSeat: string | undefined;
	monthlyPrice: string | undefined;
	monthlyDiscountPrice?: string | null;
	monthlyPricePerSeat: string | undefined;
	isLoading?: boolean;
	currency: string;
	onYearlySelected?: () => void;
	onMonthlySelected?: () => void;
}

// Skeleton component for loading state
const BillingPlanCardSkeleton: Component<{ isSelected: boolean }> = (props) => {
	return (
		<div
			class={twJoin(
				"flex flex-1 flex-col gap-1 rounded-xl border-2 p-2.5",
				props.isSelected
					? "border-interaction-violet bg-white"
					: "border-border-gray-light bg-white"
			)}
		>
			<div class="h-5 w-16 animate-pulse rounded bg-background-gray-light" />
			<div class="h-8 w-20 animate-pulse rounded bg-background-gray-light" />
			<div class="h-3 w-14 animate-pulse rounded bg-background-gray-light" />
		</div>
	);
};

const BillingPlanCard: Component<{
	option: BillingPlanOption;
	isSelected: boolean;
	onSelect: () => void;
	currency: string;
}> = (props) => {
	// Show discount_price if available, otherwise show regular price
	const displayPrice = () => props.option.discount_price ?? props.option.price;

	return (
		<button
			type="button"
			onClick={props.onSelect}
			class={twJoin(
				"flex flex-1 cursor-pointer flex-col gap-1 rounded-xl border-2 p-2.5 text-left transition-all",
				props.isSelected
					? "border-interaction-violet bg-white"
					: "border-border-gray-light bg-white hover:border-border-gray-medium"
			)}
		>
			<span class="typography-text-14-semibold text-text-primary">{props.option.label}</span>
			<span
				class={twJoin(
					"whitespace-nowrap font-medium text-2xl leading-8",
					props.isSelected ? "text-interaction-violet" : "text-text-primary"
				)}
			>
				{formatCurrency(displayPrice(), props.currency)}
			</span>
			<span class="label-10-regular whitespace-nowrap text-text-tertiary">
				{formatCurrency(props.option.price_per_seat, props.currency)}/seat
			</span>
			<Show when={props.option.discountPercent > 0}>
				<span class="caption-11-medium w-max rounded bg-status-positive px-1.5 py-0.5 text-status-accent-positive">
					Save {props.option.discountPercent}%
				</span>
			</Show>
		</button>
	);
};

export const BillingPlanSelector: Component<BillingPlanSelectorProps> = (props) => {
	const options = createMemo((): BillingPlanOption[] => [
		{
			duration: DurationMonths.Yearly,
			label: "Yearly",
			price: props.yearlyPrice,
			discount_price: props.yearlyDiscountPrice,
			price_per_seat: props.yearlyPricePerSeat,
			discountPercent: getDiscountForSeats(props.seats, DurationMonths.Yearly)
		},
		{
			duration: DurationMonths.Monthly,
			label: "Monthly",
			price: props.monthlyPrice,
			discount_price: props.monthlyDiscountPrice,
			price_per_seat: props.monthlyPricePerSeat,
			discountPercent: getDiscountForSeats(props.seats, DurationMonths.Monthly)
		}
	]);

	const nextTierInfo = createMemo(() => getNextDiscountTier(props.seats, props.selectedDuration));
	const currentDiscount = createMemo(() =>
		getDiscountForSeats(props.seats, props.selectedDuration)
	);
	const isMaxTier = createMemo(() => props.seats >= 10);

	return (
		<div class="flex w-full flex-col gap-2">
			<span class="typography-text-14-medium text-text-secondary-v2">
				Choose your billing plan
			</span>
			<div class="flex gap-2">
				<Show
					when={!props.isLoading}
					fallback={
						<>
							<BillingPlanCardSkeleton
								isSelected={props.selectedDuration === DurationMonths.Yearly}
							/>
							<BillingPlanCardSkeleton
								isSelected={props.selectedDuration === DurationMonths.Monthly}
							/>
						</>
					}
				>
					<For each={options()}>
						{(option) => (
							<BillingPlanCard
								option={option}
								isSelected={props.selectedDuration === option.duration}
								onSelect={() => {
									props.onDurationChange(option.duration);
									if (option.duration === DurationMonths.Yearly) {
										props.onYearlySelected?.();
									} else {
										props.onMonthlySelected?.();
									}
								}}
								currency={props.currency}
							/>
						)}
					</For>
				</Show>
			</div>
			<Show
				when={!isMaxTier() && nextTierInfo()}
				fallback={
					<Show when={isMaxTier() && currentDiscount() > 0}>
						<div class="flex w-full items-center justify-center rounded bg-status-accent-positive px-2 py-1.5">
							<span class="caption-12-medium text-white">
								You get {currentDiscount()}% discount{" "}
								{props.selectedDuration === DurationMonths.Yearly
									? "annually"
									: "monthly"}
							</span>
						</div>
					</Show>
				}
			>
				{(tier) => (
					<div class="flex w-full items-center justify-center rounded bg-status-accent-positive px-2 py-1.5">
						<span class="caption-12-medium text-white">
							Add {tier().seatsNeeded} more seat{tier().seatsNeeded > 1 ? "s" : ""} to
							get {tier().discount}% off{" "}
							{props.selectedDuration === DurationMonths.Yearly
								? "annually"
								: "monthly"}
						</span>
					</div>
				)}
			</Show>
		</div>
	);
};
